import { ApolloClient } from 'apollo-client'
import {
  ShareVersionHistoryFragment,
  GetVersionHistoryDocument,
  GetVersionHistoryQuery,
  GetVersionHistoryQueryVariables,
} from '@sketch/gql-types'
import uniqBy from 'lodash.uniqby'
import { dataIdFromObject } from '@sketch/graphql-cache'

type VersionHistoryListItem = NonNullable<
  GetVersionHistoryQuery['share']
>['versionHistory']['entries'][0]

const mergeEntries = (
  oldEntries: VersionHistoryListItem[],
  newEntries: VersionHistoryListItem[]
) => {
  const merged = uniqBy(
    [...newEntries, ...oldEntries],
    dataIdFromObject
  ) as VersionHistoryListItem[]
  return merged
}

const tryReadVersions = (
  client: ApolloClient<object>,
  shareIdentifier: string
) => {
  try {
    const existingData = client.readQuery<
      GetVersionHistoryQuery,
      GetVersionHistoryQueryVariables
    >({
      query: GetVersionHistoryDocument,
      variables: { shareIdentifier, after: null },
    })
    return existingData
  } catch (err) {
    // It is possible that we will receive an event when
    // versions list is still not yet loaded
    return undefined
  }
}

export const handleDocumentUploadEvent = (
  client: ApolloClient<object>,
  share?: ShareVersionHistoryFragment
) => {
  if (!share || !share.versionHistory) {
    // This should never happen
    // capture this case once error handling will be ready:
    // https://github.com/sketch-hq/Cloud/issues/1012
    return
  }

  const existingData = tryReadVersions(client, share.identifier)
  const existingAfter =
    existingData?.share?.versionHistory?.meta.after ||
    share.versionHistory.meta.after ||
    null

  const mergedEntries = mergeEntries(
    existingData?.share?.versionHistory?.entries || [],
    share.versionHistory.entries
  )

  const updatedData: GetVersionHistoryQuery = {
    __typename: 'RootQueryType',
    share: {
      ...share,
      versionHistory: {
        __typename: 'VersionHistory',
        entries: mergedEntries,
        meta: {
          ...share.versionHistory.meta,
          after: existingAfter,
        },
      },
    },
  }

  client.writeQuery<GetVersionHistoryQuery, GetVersionHistoryQueryVariables>({
    data: updatedData,
    query: GetVersionHistoryDocument,
    variables: { shareIdentifier: share.identifier, after: null },
  })
}
